import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, GripHorizontal } from 'lucide-react';

/**
 * A floating modal panel that is:
 *  - draggable   (grab the top bar)
 *  - resizable   (drag the bottom-right handle)
 *  - scrollable  (body scrolls when content overflows)
 *  - keyboard-aware (uses visualViewport so the panel is never hidden behind
 *    the on-screen keyboard — it lifts above the keyboard automatically)
 *
 * Self-contained: rendered through a Portal at <body> level so it can never be
 * clipped by a parent's overflow/transform. Does not touch any other code.
 */
export const DraggableModal = ({
  open,
  onClose,
  title,
  children,
  testId = 'draggable-modal',
  minWidth = 300,
  minHeight = 220,
  initialWidth = 440,
}) => {
  const panelRef = useRef(null);
  const dragRef = useRef(null);   // { startX, startY, origX, origY }
  const resizeRef = useRef(null); // { startX, startY, origW, origH }
  const [pos, setPos] = useState(null);   // { x, y }
  const [size, setSize] = useState(null); // { w, h }

  const vv = () => (typeof window !== 'undefined' ? window.visualViewport : null);
  const vpW = () => (vv()?.width || window.innerWidth);
  const vpH = () => (vv()?.height || window.innerHeight);
  const vpTop = () => (vv()?.offsetTop || 0);

  // Initialise position/size when opened.
  useEffect(() => {
    if (!open) return;
    const w = Math.min(initialWidth, vpW() - 24);
    const h = Math.min(Math.round(vpH() * 0.6), vpH() - 120);
    const x = Math.max(12, Math.round((vpW() - w) / 2));
    // Open a bit ABOVE centre so the keyboard (bottom half) doesn't cover it.
    const y = Math.max(vpTop() + 12, Math.round(vpTop() + (vpH() - h) / 2 - 60));
    setSize({ w, h: Math.max(minHeight, h) });
    setPos({ x, y });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clamp the panel inside the (possibly keyboard-shrunk) visual viewport.
  // Also cap its HEIGHT to the available viewport so, when the on-screen
  // keyboard opens (PWA/mobile), the whole panel — including the submit button
  // — stays above the keyboard and its body scrolls internally instead of
  // hiding fields behind the keyboard.
  const clampIntoView = useCallback(() => {
    const availH = vpH() - 16;
    setSize((s) => {
      if (!s) return s;
      const capped = Math.max(minHeight, Math.min(s.h, availH));
      return capped === s.h ? s : { ...s, h: capped };
    });
    setPos((p) => {
      if (!p) return p;
      const el = panelRef.current;
      const h = Math.min(el ? el.offsetHeight : (size?.h || minHeight), availH);
      const w = el ? el.offsetWidth : (size?.w || initialWidth);
      const maxX = vpW() - w - 8;
      const maxY = vpTop() + vpH() - h - 8;
      const minY = vpTop() + 8;
      return {
        x: Math.min(Math.max(8, p.x), Math.max(8, maxX)),
        y: Math.min(Math.max(minY, p.y), Math.max(minY, maxY)),
      };
    });
  }, [size, minHeight, initialWidth]);

  // React to keyboard open/close (visualViewport resize) → keep panel visible.
  useEffect(() => {
    if (!open) return;
    const v = vv();
    const onResize = () => clampIntoView();
    if (v) {
      v.addEventListener('resize', onResize);
      v.addEventListener('scroll', onResize);
    }
    window.addEventListener('resize', onResize);
    return () => {
      if (v) {
        v.removeEventListener('resize', onResize);
        v.removeEventListener('scroll', onResize);
      }
      window.removeEventListener('resize', onResize);
    };
  }, [open, clampIntoView]);

  // ---- Dragging ----
  const onDragStart = (clientX, clientY) => {
    if (!pos) return;
    dragRef.current = { startX: clientX, startY: clientY, origX: pos.x, origY: pos.y };
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const nx = d.origX + (e.clientX - d.startX);
    const ny = d.origY + (e.clientY - d.startY);
    const el = panelRef.current;
    const w = el ? el.offsetWidth : initialWidth;
    const h = el ? el.offsetHeight : minHeight;
    setPos({
      x: Math.min(Math.max(8, nx), Math.max(8, vpW() - w - 8)),
      y: Math.min(Math.max(vpTop() + 8, ny), Math.max(vpTop() + 8, vpTop() + vpH() - h - 8)),
    });
  };
  const onDragEnd = () => {
    dragRef.current = null;
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
  };

  // ---- Resizing ----
  const onResizeStart = (e, clientX, clientY) => {
    e.stopPropagation();
    if (!size) return;
    resizeRef.current = { startX: clientX, startY: clientY, origW: size.w, origH: size.h };
    document.addEventListener('pointermove', onResizeMove);
    document.addEventListener('pointerup', onResizeEnd);
  };
  const onResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    const nw = Math.max(minWidth, Math.min(r.origW + (e.clientX - r.startX), vpW() - 16));
    const nh = Math.max(minHeight, Math.min(r.origH + (e.clientY - r.startY), vpH() - 16));
    setSize({ w: nw, h: nh });
  };
  const onResizeEnd = () => {
    resizeRef.current = null;
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
  };

  if (!open || !pos || !size) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/40"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid={`${testId}-overlay`}
    >
      <div
        ref={panelRef}
        className="absolute bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
        style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
        onPointerDown={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {/* Drag bar */}
        <div
          className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200 cursor-move select-none flex-shrink-0 touch-none"
          onPointerDown={(e) => { e.preventDefault(); onDragStart(e.clientX, e.clientY); }}
          data-testid={`${testId}-draghandle`}
        >
          <GripHorizontal size={16} className="text-slate-400" />
          <h3 className="font-bold text-sm text-slate-800 flex-1 truncate">{title}</h3>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onClose}
            className="p-1 hover:bg-slate-200 rounded"
            data-testid={`${testId}-close`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4" data-testid={`${testId}-body`}>
          {children}
        </div>

        {/* Resize handle (bottom-right) */}
        <div
          className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-end justify-end p-0.5 touch-none"
          onPointerDown={(e) => { e.preventDefault(); onResizeStart(e, e.clientX, e.clientY); }}
          data-testid={`${testId}-resize`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" className="text-slate-400">
            <path d="M11 1 L1 11 M11 5 L5 11 M11 9 L9 11" stroke="currentColor" strokeWidth="1.2" fill="none" />
          </svg>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DraggableModal;
